import React, { useState, useEffect } from "react";
import Axios from "axios";
import { useParams } from "react-router-dom";
import "../../css/style.css";
import Constant from "../Constant";
import { getAccessToken } from "../../hooks/AccessToken";

const ViewSubmissions = () => {
  const { id: contestId } = useParams();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [contestDetails, setContestDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const itemsPerPage = 25;
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // 'desc' for most likes first
  const [sortField, setSortField] = useState("likes"); // Add this with other state declarations

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setIsLoading(true);
        let token = localStorage.getItem("loginDetails");
        const response = await Axios.get(
          `https://api.storytent.in/api/contest/getParticipantsCount/${contestId}`,
          { headers: { token: token } }
        );

        const res = await Axios.get(
          `https://api.storytent.in/api/contest/getDataById/${contestId}`,
          {
            headers: { token: token },
          }
        );
        setContestDetails(res.data.data[0]);

        if (response.data) {
          let allSubmissions = [];
          if (response.data.stories && response.data.stories.length > 0) {
            allSubmissions = [
              ...allSubmissions,
              ...response.data.stories.map((story) => ({
                ...story,
              })),
            ];
          }
          if (response.data.poems && response.data.poems.length > 0) {
            allSubmissions = [
              ...allSubmissions,
              ...response.data.poems.map((poem) => ({ ...poem })),
            ];
          }

          // Fetch user emails for all submissions
          const submissionsWithEmail = await Promise.all(
            allSubmissions.map(async (submission) => {
              const userData = await getUserMail(submission.userId);
              return {
                ...submission,
                userEmail: userData?.email || "Email not available",
              };
            })
          );

          setSubmissions(submissionsWithEmail);
          setFilteredSubmissions(submissionsWithEmail);
        }
      } catch (err) {
        console.error("Error fetching submissions:", err);
      } finally {
        setIsLoading(false);
      }
    };

    const getUserMail = async (userId) => {
      try {
        let token = localStorage.getItem("loginDetails");
        const response = await Axios.get(
          `https://api.storytent.in/api/getUserDataById/${userId}`,
          { headers: { token: token } }
        );
        if (response.data) {
          // console.log("User data:", response.data.data);
          return response.data.data;
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        return null;
      }
    };

    if (contestId) {
      fetchSubmissions();
    }
  }, [contestId]);

  const deletePoem = async (id) => {
    try {
      let token = localStorage.getItem("loginDetails");
      const response = await Axios.delete(
        `https://api.storytent.in/api/poem/${id}`,
        { headers: { token: token } }
      );
      if (response.data) {
        alert("Poem deleted successfully");
        const updatedPoems = submissions.filter((poem) => poem._id !== id);
        setSubmissions(updatedPoems);
        setFilteredSubmissions(updatedPoems);
      }
    } catch (err) {
      console.error("Error deleting poem:", err);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSubmissions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages =
    filteredSubmissions.length > 0
      ? Math.ceil(filteredSubmissions.length / itemsPerPage)
      : 1;

  const exportData = async () => {
    try {
      let token = getAccessToken();
      const { data } = await Axios.get(
        Constant.apiBasePath +
          `contest/getParticipantsCount/${contestId}?exportData=true`,
        { headers: { token: token } }
      );

      if (data.status === Constant.statusSuccess) {
        let csvString = "";
        const fileName = "contest_submissions.csv";

        const poems =
          typeof data.poems === "string" ? data.poems.split("\n") : [];
        const stories =
          typeof data.stories === "string"
            ? data.stories.split("\n").slice(1)
            : [];

        // Combine poems and stories into a single CSV string
        if (poems.length > 0) {
          csvString += poems.join("\n");
        }
        if (stories.length > 0) {
          csvString += (csvString ? "\n" : "") + stories.join("\n");
        }

        if (csvString) {
          // Create blob with BOM for Excel UTF-8 support
          const BOM = "\uFEFF";
          const blob = new Blob([BOM + csvString], {
            type: "text/csv;charset=utf-8;",
          });

          // Create URL for blob
          const url = window.URL.createObjectURL(blob);

          // Create temporary link element
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);

          // Append to document, click, and clean up
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Release the blob URL
          window.URL.revokeObjectURL(url);
        } else {
          alert("No data available for export");
        }
      } else {
        alert(data.message || "Failed to export data");
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export data");
    }
  };
  // Add new state for sorting

  // Add sorting function
  // Update the sortByLikes function to handle the new sorting mechanism
  const sortByLikes = (items) => {
    return [...items].sort((a, b) => {
      if (sortField === "likes") {
        const likesA = a.likes?.length || 0;
        const likesB = b.likes?.length || 0;
        return sortOrder === "desc" ? likesB - likesA : likesA - likesB;
      }
      return 0;
    });
  };

  // Modify the filter and search logic to include sorting
  const handleFilterChange = (event) => {
    const language = event.target.value;
    setSelectedLanguage(language);
    const filtered =
      language === ""
        ? submissions
        : submissions.filter((sub) => sub.language === language);
    setFilteredSubmissions(sortByLikes(filtered));
    setCurrentPage(1);
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);

    // If both search and language filters are active
    const languageFiltered = selectedLanguage
      ? submissions.filter((sub) => sub.language === selectedLanguage)
      : submissions;

    // Then filter by search term
    const searchFiltered = term
      ? languageFiltered.filter((sub) => {
          const username = (sub.username || "").toLowerCase();
          const description = (sub.description || "").toLowerCase();
          const searchWords = term.toLowerCase().split(" ");
          return searchWords.every(
            (word) => username.includes(word) || description.includes(word)
          );
        })
      : languageFiltered;

    // Apply sorting after filtering
    setFilteredSubmissions(sortByLikes(searchFiltered));
    setCurrentPage(1);
  };

  // Add a toggle sort order function
  const toggleSortOrder = () => {
    const newOrder = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(newOrder);
    setFilteredSubmissions(sortByLikes(filteredSubmissions));
  };

  // Add this useEffect to sort submissions when they are initially loaded
  useEffect(() => {
    if (submissions.length > 0) {
      setFilteredSubmissions(sortByLikes(submissions));
    }
  }, [submissions, sortOrder]);

  return (
    <div className="content-page contest-submissions-page">
      <div className="content page-content-spaced">
        <div className="event-management-header">
          <div className="header-content">
            <div className="header-title">
              <h4 className="page-title-heading">Contest Submissions</h4>
              <p className="header-subtitle">
                Review participant entries, filter quickly, and export reports.
              </p>
            </div>
            <button
              className="btn btn-success export-content-btn contest-submissions-export"
              onClick={exportData}
            >
              Export Data
            </button>
          </div>
        </div>

        <div className="event-management-body">
          <div className="card contest-submissions-card contest-like-card">
            <div className="card-body">
              <div className="filter-section contest-submissions-filter-section">
                <div className="filter-group contest-submissions-filter">
                  <label>Language</label>
                  <select
                    className="form-control filter-select"
                    value={selectedLanguage}
                    onChange={handleFilterChange}
                  >
                    <option value="">All</option>
                    <option value="English">English</option>
                    <option value="Hindi">Hindi</option>
                  </select>
                </div>
                <div className="filter-group contest-submissions-filter">
                  <label>Sort by</label>
                  <select
                    className="form-control filter-select sort-select"
                    value={`${sortField}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split("-");
                      setSortField(field);
                      setSortOrder(order);
                      setFilteredSubmissions(sortByLikes([...filteredSubmissions]));
                    }}
                  >
                    <option value="likes-desc">Likes (Most first)</option>
                    <option value="likes-asc">Likes (Least first)</option>
                  </select>
                </div>
                <div className="contest-submissions-search contest-submissions-search-group">
                  <label>Search user or description</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by username or description..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>

              <div className="table-wrapper contest-submissions-table-responsive contest-like-table-responsive">
                <table className="table event-table contest-submissions-table contest-like-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>UserName</th>
                      <th>Email</th>
                      <th>Description</th>
                      <th>Thumbnail</th>
                      <th className="likes-col">Likes</th>
                      <th>Language</th>
                      {contestDetails.mobileNumber && <th>Phone Number</th>}
                      {contestDetails.idCard && <th>College ID</th>}
                      {contestDetails.collegeName && <th>College Name</th>}
                      {contestDetails.collegeAddress && <th>College Address</th>}
                      {contestDetails.refId && <th>Ref ID</th>}
                      <th>CreatedAt</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="12" className="text-center table-loading-cell">
                          <div className="loader-container">
                            <div className="loader"></div>
                            <p>Loading submissions...</p>
                          </div>
                        </td>
                      </tr>
                    ) : currentItems.length > 0 ? (
                      currentItems.map((submission, index) => (
                        <tr key={index} className="table-row">
                          <td className="namecol">{submission.name}</td>
                          <td>{submission.username}</td>
                          <td>{submission.userEmail}</td>
                          <td className="description">
                            <div className="description-text">
                              {submission.description || ""}
                            </div>
                          </td>
                          <td>
                            <img
                              src={submission.thumbnail}
                              alt="Thumbnail"
                              style={{ width: 50, height: 50 }}
                            />
                          </td>
                          <td className="likes-col">
                            {submission.likes.length > 0
                              ? submission.likes.length
                              : 0}
                          </td>
                          <td>{submission.language}</td>
                          {contestDetails.mobileNumber && (
                            <td>{submission.mobileNumber || "-"}</td>
                          )}
                          {contestDetails.idCard && (
                            <td>
                              <img
                                src={submission.idCard}
                                alt="Thumbnail"
                                style={{
                                  minWidth: 50,
                                  maxWidth: 80,
                                  minHeight: 50,
                                  maxHeight: 80,
                                }}
                              />
                            </td>
                          )}
                          {contestDetails.collegeName && (
                            <td>{submission.collegeName}</td>
                          )}
                          {contestDetails.collegeAddress && (
                            <td>{submission.collegeAddress}</td>
                          )}
                          {contestDetails.refId && (
                            <td>{submission.refId}</td>
                          )}
                          <td>{submission.createdAt}</td>
                          <td className="actions-col">
                            <button
                              className="contest-icon-btn danger submission-action-btn"
                              onClick={() => {
                                if (window.confirm("Delete the poem?")) {
                                  deletePoem(submission._id);
                                }
                              }}
                            >
                              <i
                                className="mdi mdi-delete"
                                title="Delete Poem"
                              ></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center table-loading-cell">
                          No submissions available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {!isLoading && filteredSubmissions.length > 0 && (
                  <div className="pagination contest-submissions-pagination">
                    <button
                      type="button"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={currentPage === 1 ? "disabled" : "paginate-link"}
                    >
                      Previous
                    </button>
                    <button type="button" className="page-number">
                      {`${currentPage}/${totalPages}`}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={currentPage === totalPages ? "disabled" : "paginate-link active"}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewSubmissions;
